import React, { createContext, useContext, useEffect, useState } from 'react'
import { createBrowserHistory } from 'history'

// Create the context
const RouterHistoryContext = createContext()

// Custom hook to use the context
export const useRouterHistory = () => {
  const context = useContext(RouterHistoryContext)
  if (!context) {
    throw new Error('useRouterHistory must be used within a RouterHistoryProvider')
  }
  return context
}

const MAX_HISTORY_LENGTH = 20 // Maximum number of pathnames to keep in history

// Create and export custom history object
export const customHistory = createBrowserHistory()

export const RouterHistoryProvider = ({ children }) => {
  const [historyStack, setHistoryStack] = useState(() => {
    // Initialize history stack from local storage if available
    if (sessionStorage) {
      const savedHistoryStack = sessionStorage.getItem('historyStack')

      if (savedHistoryStack) {

        try {
          const historyStack = JSON.parse(savedHistoryStack)

          if (Array.isArray(historyStack)) {
            return historyStack
          } else {
            return [customHistory.location.pathname]
          }
        } catch(_) {
          return [customHistory.location.pathname]
        }

      } else {
        return [customHistory.location.pathname]
      }
    } else {
      return [customHistory.location.pathname]
    }
  })

  useEffect(() => {
    // Save history stack to local storage whenever it changes
    if (sessionStorage) {
      sessionStorage.setItem('historyStack', JSON.stringify(historyStack))
    }
  }, [historyStack])

  useEffect(() => {
    const teardown = customHistory.listen((location, action) => {
      setTimeout(() => {
        setHistoryStack((prevStack) => {
          if (action === 'PUSH') {
            return [...prevStack.slice(-MAX_HISTORY_LENGTH + 1), location.pathname]
          } else if (action === 'POP') {
            let currentIndex = -1

            for (let i = 0; i < prevStack.length; i++) { // find last one
              if (prevStack[i] === location.pathname) {
                currentIndex = i
              }
            }

            if (currentIndex === -1) {
              return prevStack.length > 1 ? prevStack.slice(0, -1) : prevStack
            } else {
              return prevStack.slice(0, currentIndex + 1).slice(-MAX_HISTORY_LENGTH)
            }
          }
          return prevStack
        })

        if (action !== 'POP') {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant',
          })
        }
      }, 200)
    })

    return () => {
      teardown()
    }
  }, [])

  const historyLength = historyStack.length

  return (
    <RouterHistoryContext.Provider value={{ historyStack, historyLength, customHistory }}>
      {children}
    </RouterHistoryContext.Provider>
  )
}
